import { fabric } from '~/utils/editor/fabric'
import { EditorObject } from '~/utils/editor/object.class'
import { EditorData } from '~/static/enums/editor-state'
import { Background as BackgroundModel } from '~/utils/editor/background.class'
import { AnyActualProp, migrate } from '~/utils/editor/versioning/migrators'
import { migrateBackground } from '~/utils/editor/versioning/background'
import { jsonParseWithUndefined } from '~/utils/Helpers'
import { ObjectMode, ObjectType } from '~/static/enums/object-type'
import { Icon } from '../objects/icon.class'
import { ImageObject } from '../objects/image.class'
import { Line } from '../objects/line.class'
import { SvgFigure } from '../objects/svg-figure.class'
import { Text } from '../objects/text.class'
import * as fabricTypes from 'fabric'

export const getCanvasPreview = async (
  data: EditorData,
  width?: number,
  height?: number,
  previewMode: boolean = false,
): Promise<string> => {
  const { objects, error } = initObjectsFromJson(data.objects)
  const background = initBackgroundFromJson(data.background)

  // Calculate scalefactor for small image
  const widthScaleFactor = (width ?? background.width) / background.width
  const heightScaleFactor = (height ?? background.height) / background.height
  const scalefactor = widthScaleFactor > heightScaleFactor ? heightScaleFactor : widthScaleFactor

  const canvas = new fabric.StaticCanvas(undefined, {
    width: background.width,
    height: background.height,
  })
  canvas.setZoom(scalefactor)

  let objectsMode = ObjectMode.Export
  if (previewMode) {
    objectsMode = ObjectMode.Editing
    // Generate chess background for preview
    const chessStatic = new fabric.StaticCanvas(undefined, { enableRetinaScaling: false })
    chessStatic.setDimensions({
      width: 50 / scalefactor,
      height: 50 / scalefactor,
    })
    chessStatic.renderAll()
    chessStatic.set('backgroundColor', '#ebecf0')
    const greyRect = new fabric.Rect({
      width: 25 / scalefactor,
      height: 25 / scalefactor,
      left: 0,
      top: 0,
      strokeWidth: 0,
    })
    greyRect.set('fill', '#b0b0b0')
    const lightGreyRect = new fabric.Rect({
      width: 25 / scalefactor,
      height: 25 / scalefactor,
      left: 25 / scalefactor,
      top: 25 / scalefactor,
      strokeWidth: 0,
    })
    lightGreyRect.set('fill', '#b0b0b0')

    chessStatic.add(greyRect)
    chessStatic.add(lightGreyRect)
    chessStatic.add(greyRect)
    chessStatic.add(lightGreyRect)
    chessStatic.calcOffset()
    chessStatic.renderAll()

    // Added chess background to canvas
    const opacityBack = new fabric.Rect({
      width: background.width,
      height: background.height,
      rx: background.borderRadius,
      ry: background.borderRadius,
      fill: new fabric.Pattern({
        source: chessStatic.getElement(),
        repeat: 'repeat',
      }),
    })
    canvas.add(opacityBack)
  }

  canvas.add(background.object)

  if (error) {
    if (process.server) {
      console.log(error)
    } else {
      console.log(error + ', card: ' + window.location.href)
    }
  }

  for (const object of objects) {
    await object.load(objectsMode)
    await object.draw()
    canvas.add(object.object)
  }
  canvas.setZoom(scalefactor)

  const clipPath = new fabric.Rect({
    absolutePositioned: true,
    width: background.width,
    height: background.height,
    rx: background.borderRadius,
    ry: background.borderRadius,
  })
  canvas.clipPath = clipPath
  canvas.renderAll()

  return canvas.toDataURL({
    width: background.width * scalefactor,
    height: background.height * scalefactor,
    left: 0,
    top: 0,
    format: 'png',
    multiplier: 1,
  })
}

export const getCanvasExport = async (
  data: EditorData,
  width?: number,
  height?: number,
) => {
  const canvas = await getStaticCanvas(data, width, height)
  if (!('getNodeCanvas' in canvas) || typeof canvas.getNodeCanvas !== 'function') {
    throw createError('Node canvas not defined')
  }
  // 2. ГЛАВНЫЙ МОМЕНТ: Достаем нативный node-canvas из под капота Fabric
  // Fabric хранит его внутри и дает метод для доступа
  const nodeCanvas = canvas.getNodeCanvas()

  // 4. Создаем поток PNG.
  // Это НЕ создает весь файл в памяти. Это создает "трубу", по которой
  // пиксели будут течь по мере сжатия.
  const stream = nodeCanvas.createPNGStream({
    compressionLevel: 6, // От 0 до 9. По умолчанию 6. Меньше = быстрее, но файл больше.
    filters: nodeCanvas.PNG_ALL_FILTERS,
  })
  
  // ВАЖНО: Очистка памяти.
  // Проблема стримов в том, что мы не знаем точно, когда они закончатся.
  // Но для node-canvas критично вызвать canvas.dispose(), иначе потечет C++ память.
  
  stream.on('end', () => {
    // Освобождаем память после того, как отправка завершена
    canvas.dispose() 
  })
  
  stream.on('error', (err: unknown) => {
    console.error('Ошибка стрима', err)
    canvas.dispose()
  })

  // 5. Пайпинг (Piping) потока прямо в ответ (response)
  // В Nuxt 3 (Nitro) мы можем просто вернуть stream, и он сам сделает pipe
  return stream
}

const getStaticCanvas = async (
  data: EditorData,
  width?: number,
  height?: number,
  previewMode: boolean = false,
): Promise<fabricTypes.StaticCanvas> => {
  const { objects, error } = initObjectsFromJson(data.objects)
  const background = initBackgroundFromJson(data.background)

  // Calculate scalefactor for small image
  const widthScaleFactor = (width ?? background.width) / background.width
  const heightScaleFactor = (height ?? background.height) / background.height
  const scalefactor = widthScaleFactor > heightScaleFactor ? heightScaleFactor : widthScaleFactor

  const canvas = new fabric.StaticCanvas(undefined, {
    width: background.width * scalefactor,
    height: background.height * scalefactor,
  })
  canvas.setZoom(scalefactor)

  let objectsMode = ObjectMode.Export
  if (previewMode) {
    objectsMode = ObjectMode.Editing
    // Generate chess background for preview
    const chessStatic = new fabric.StaticCanvas(undefined, { enableRetinaScaling: false })
    chessStatic.setDimensions({
      width: 50 / scalefactor,
      height: 50 / scalefactor,
    })
    chessStatic.renderAll()
    chessStatic.set('backgroundColor', '#ebecf0')
    const greyRect = new fabric.Rect({
      width: 25 / scalefactor,
      height: 25 / scalefactor,
      left: 0,
      top: 0,
      strokeWidth: 0,
    })
    greyRect.set('fill', '#b0b0b0')
    const lightGreyRect = new fabric.Rect({
      width: 25 / scalefactor,
      height: 25 / scalefactor,
      left: 25 / scalefactor,
      top: 25 / scalefactor,
      strokeWidth: 0,
    })
    lightGreyRect.set('fill', '#b0b0b0')

    chessStatic.add(greyRect)
    chessStatic.add(lightGreyRect)
    chessStatic.add(greyRect)
    chessStatic.add(lightGreyRect)
    chessStatic.calcOffset()
    chessStatic.renderAll()

    // Added chess background to canvas
    const opacityBack = new fabric.Rect({
      width: background.width,
      height: background.height,
      rx: background.borderRadius,
      ry: background.borderRadius,
      fill: new fabric.Pattern({
        source: chessStatic.getElement(),
        repeat: 'repeat',
      }),
    })
    canvas.add(opacityBack)
  }

  canvas.add(background.object)

  if (error) {
    if (process.server) {
      console.log(error)
    } else {
      console.log(error + ', card: ' + window.location.href)
    }
  }

  for (const object of objects) {
    await object.load(objectsMode).catch((e) => {
      console.log(e)
    })
    await object.draw()
    canvas.add(object.object)
  }
  canvas.setZoom(scalefactor)

  const clipPath = new fabric.Rect({
    absolutePositioned: true,
    width: background.width,
    height: background.height,
    rx: background.borderRadius,
    ry: background.borderRadius,
  })
  canvas.clipPath = clipPath
  canvas.renderAll()

  return canvas
}

export const initBackgroundFromJson = (jsonBackground: string): BackgroundModel => {
  return new BackgroundModel(migrateBackground(JSON.parse(jsonBackground)))
}

export const initObjectsFromJson = (jsonObjects: string[]): {
  objects: EditorObject[]
  error: string | undefined
} => {
  let error: string | undefined = undefined
  const objects = jsonObjects.map((objectJson) => {
    try {
      return createObjectFromJson(objectJson)
    } catch (e) {
      console.log(e)
      error = (e instanceof Error) ? e.message : (typeof e === 'string') ? e : JSON.stringify(e)
      return undefined
    }
  }).filter((object) => { return object !== undefined }) as EditorObject[]
  return { objects, error }
}

export const createObjectFromJson = (object: string): EditorObject => {
  const data: AnyActualProp = jsonParseWithUndefined<AnyActualProp>(object)
  return editorObjectFactory(data)
}

export const editorObjectFactory = (data: AnyActualProp): EditorObject => {
  if (!data.type) {
    throw createError('Invalid object type: ' + data.type)
  }
  const actualData = migrate(data.type, data)

  let model: EditorObject
  switch (actualData.type) {
  case ObjectType.Icon:
    model = new Icon(actualData)
    break
  case ObjectType.Image:
    model = new ImageObject(actualData)
    break
  case ObjectType.Line:
    model = new Line(actualData)
    break
  case ObjectType.SvgFigure:
    model = new SvgFigure(actualData)
    break
  case ObjectType.Text:
    model = new Text(actualData)
    break
  default:
    throw createError('Invalid object type: ' + data.type)
  }

  return model
}
